import axios from 'axios';

export type MutationFailure = 'rejected' | 'ambiguous';

/**
 * Whether a failed write certainly did not commit. A 4xx response (except
 * 408 Request Timeout) means the server answered and refused the request,
 * e.g. 422 PROPERTY_SIZE_REQUIRED or a 400 validation error: the user can fix
 * the input and resubmit safely. No response (network error, timeout, lost
 * response), a 5xx, or a non-HTTP error is ambiguous: the write may have
 * committed, so the caller must re-read before offering it again.
 */
export function classifyMutationFailure(err: unknown): MutationFailure {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status !== undefined && status >= 400 && status < 500 && status !== 408) return 'rejected';
  }
  return 'ambiguous';
}

/**
 * A mutation response confirms the write. Refresh failure must not make a
 * caller offer the same write again, especially for billable job lines.
 */
export async function mutateThenRefresh(
  mutate: () => Promise<unknown>,
  acknowledge: () => void,
  refresh: () => Promise<unknown>
): Promise<unknown | null> {
  await mutate();
  acknowledge();
  try {
    await refresh();
    return null;
  } catch (error) {
    return error;
  }
}
